import { useEffect, useState, useRef } from 'react'
import styles from './Explorer.module.scss'
import { useSelector } from 'react-redux'
import { ScoopText, ScoopDrawer, useToast, ScoopTab, ScoopButton, ScoopSpinner } from '../../ui/'
import { Server } from '../../../api/Server'
import ChartState, { backwardsFlag } from '../../Insights/ChartState.js'
import { DataTab } from './DrawerTabs/DataTab/DataTab'
import { ConfigTab } from './DrawerTabs/ConfigTab/ConfigTab'
import { Insight } from '../../Insights/Insight'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from '../../../router/routes'
import { removeFontScaleFactors, saveInsight } from '../../Objects/Insight/InsightAPI'
import { useApi } from '../../../api/api'
import { FilterTab } from './DrawerTabs/FilterTab/FilterTab'
import ChartPlaceholder from '../../../assets/explorer/chart-placeholder.webp'
import {
    FloppyDiskIcon,
    FloppyDiskBackIcon,
    FolderOpenIcon,
    MagicWandIcon,
    ChartBarIcon,
} from '@phosphor-icons/react'
import { StyleTab } from './DrawerTabs/StyleTab/StyleTab'
import { DEFAULT_CHART_PREFERENCES, TITLE_DEFAULT_VALUES } from './DrawerTabs/StyleTab/styleConsts'
import { ScoopTheme } from '../../Insights/Style'
import { cloneDeep, merge } from 'lodash'
import { useWorkspaceMetadata } from '../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { EmptyDataset } from '../../EmptyDataset/EmptyDataset'
import { getWorkspaceTheme } from '../../Insights/utils'
import { HelpBotPromptButton } from '../../HelpBot/components/HelpBotPromptButton/HelpBotPromptButton'
import { HELP_BOT_PROMPTS } from '../../HelpBot/helpBotPrompts'
import { useModal } from '../../modals/useModal'
import { MODALS } from '../../modals/ModalRegistry'
import { EVENTS, trackEvent } from '../../../events'
import { useIsTourWorkspace } from '../../../hooks/useIsTourWorkspace'
import { useOnboarding, ONBOARDING_STEPS } from '../../../api/hooks/onboarding/useOnboarding'

const leftDrawerTabs = [
    { label: 'Data', key: 'data' },
    { label: 'Filters', key: 'filters' },
]

const rightDrawerTabs = [
    { label: 'Properties', key: 'properties' },
    { label: 'Style', key: 'style' },
]

export const Explorer = () => {
    const { state } = useLocation()
    const navigate = useNavigate()
    const isTourWorkspace = useIsTourWorkspace()
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const userID = useSelector((state) => state.auth.userID)
    const token = useSelector((state) => state.auth.token)
    const [selectedLeftTab, setSelectedLeftTab] = useState(leftDrawerTabs[0].key)
    const [selectedRightTab, setSelectedRightTab] = useState(rightDrawerTabs[0].key)
    const [server, setServer] = useState(new Server(workspaceID, userID, token))
    const [dataTabOpen, setDataTabOpen] = useState(true)
    const [configTabOpen, setConfigTabOpen] = useState(true)
    const [insightToSave, setInsightToSave] = useState(null)
    const [insightResult, setInsightResult] = useState(null)
    const [loadingTableInsight, setLoadingTableInsight] = useState(false)
    const [loadingChartInsight, setLoadingChartInsight] = useState(false)
    const [config, setConfig] = useState(state?.editmodeConfig || ChartState.getDefaultConfig())
    const [chartState, setChartState] = useState(new ChartState(server, config, setConfig))
    const [advanced, setAdvanced] = useState(!!config.filter || config.categoryAxis !== 'Time')
    const [analyzeChanges, setAnalyzeChanges] = useState(false)
    const [filterSelectedColumn, setFilterSelectedColumn] = useState(undefined)
    const [filterOpen, setFilterOpen] = useState(undefined)
    const [columnLoading, setColumnLoading] = useState(false)
    const [dateFlag, setDateFlag] = useState(false)
    const [isExistingInsight, setIsExistingInsight] = useState(false)
    const [worksheetMetrics, setWorksheetMetrics] = useState([])
    const toast = useToast()
    const { postData } = useApi()
    const {
        workspaceMetadata,
        refetch,
        isLoading: workspaceMetadataLoading,
    } = useWorkspaceMetadata()

    // Modal Manager hooks
    const saveModal = useModal(MODALS.COMMON.SAVE)
    const openModal = useModal(MODALS.COMMON.OPEN)
    const generateModal = useModal(MODALS.COMMON.GENERATE_INSIGHT)
    const timeRangeModal = useModal(MODALS.COMMON.TIME_RANGE)
    const resetInsightModal = useModal(MODALS.EXPLORER.RESET_INSIGHT)
    const saveChangesModal = useModal(MODALS.COMMON.SAVE_CHANGES)

    // Onboarding tracking - check if user needs to complete step 3
    const { isStepCompleted } = useOnboarding()
    const isOnboardingStep3 =
        isStepCompleted(ONBOARDING_STEPS.OPEN_CHART) &&
        !isStepCompleted(ONBOARDING_STEPS.SAVE_TO_CANVAS)

    useEffect(() => {
        const newServer = new Server(workspaceID, userID, token)
        setServer(newServer)
        const newChartState = new ChartState(newServer, config, setConfig)
        setChartState(newChartState)
    }, [userID, token, workspaceID])

    useEffect(() => {
        if (state?.editmodeConfig) loadFromSavedInsight(config, state?.fromChat)
    }, [chartState])

    useEffect(() => {
        // update metrics if open from saved insight
        if (config.worksheetID && worksheetMetrics.length === 0) {
            const action = {
                action: 'getTimeSeries',
                metrics: [],
                calendarType: config.calendarType,
                period: config.period,
                split: true,
                process: false,
                timeRange: config.timeRange,
                worksheetID: config.worksheetID,
            }
            if (config.sheetName) action.sheetName = config.sheetName
            if (config.rangeName) action.rangeName = config.rangeName
            server.postData(action, (r) => {
                setWorksheetMetrics(r.sheetColumns.filter((col) => col.isMeasure))
                const newConfig = { ...config }
                newConfig.worksheetColumns = r.sheetColumns
                setConfig(newConfig)
            })
        }
    }, [config.worksheetID, server])

    const handleReset = () => {
        chartState.clear()
        setIsExistingInsight(false)
        setInsightToSave(null)
        if (state?.editmodeConfig) navigate('.', { replace: true })
    }

    function handleSaveInsight() {
        trackEvent(EVENTS.EXPLORER.SAVE_AS)
        const configToSave = cloneDeep(config)
        configToSave.selectedDates = Object.fromEntries(configToSave.selectedDates)
        configToSave.seriesTypeMap = Array.from(configToSave.seriesTypeMap)
        if (configToSave.view === 'chart' && chartState.valueAxis)
            configToSave.metricsOrder = chartState.valueAxis.map((a) => ({
                name: a.name,
                position: a.position,
            }))
        if (configToSave.view !== 'kpi') {
            configToSave.styleOverrides.dimensions = {
                width: window.innerWidth - 600,
                height: window.innerHeight - 112,
            }
        } else {
            configToSave.styleOverrides.dimensions = {
                width: 200,
                height: 200,
            }
        }
        const insight = {
            insightName: insightResult ? insightResult.insightName : '',
            insightKey: null,
            workspaceID: server.workspaceID,
            savedObject: JSON.stringify(configToSave),
        }
        setInsightToSave(insight)
        saveModal.show({
            insight,
            workspaceMetadata,
        })
    }

    function handleAdvancedSwitch() {
        setAdvanced(!advanced)
        const newConfig = { ...config }
        setConfig(newConfig)
    }

    function getWorkspaceMetadata(chartState, callback) {
        refetch().then((result) => {
            chartState.workspaceMetadata = result
            setChartState(chartState)
            callback && callback(result)
        })
    }

    const loadFromSavedInsight = (insight, fromChat) => {
        if (!insight) {
            handleReset()
            return
        }
        trackEvent(EVENTS.EXPLORER.OPEN_SUMMARY)
        if (insight.view === 'chart') setLoadingChartInsight(true)
        if (Array.isArray(insight.seriesTypeMap))
            insight.seriesTypeMap = new Map(insight.seriesTypeMap)
        if (!(insight.seriesTypeMap instanceof Map)) insight.seriesTypeMap = new Map()
        if (!insight.usedDrillAttributes) insight.usedDrillAttributes = []
        // START temp backwards stuff
        if (!insight.selectedTableKpis) {
            insight.selectedTableColumns = []
            insight.selectedTableKpis = []
            insight.selectedTables = []
            insight.tableTimeSeries = true
            if (insight.view === 'table') {
                insight.selectedItems.forEach((item) => {
                    if (!insight.selectedTables.includes(item.reportSeriesTableID)) {
                        insight.selectedTables.push(item.reportSeriesTableID)
                    }
                    if (
                        !insight.selectedTableColumns.includes(item.kpi) ||
                        !insight.selectedTableColumns.includes(item.columnName)
                    ) {
                        if (item.columnName) {
                            // Measure
                            workspaceMetadata?.inboxes?.forEach((inbox) => {
                                inbox.tables.forEach((table) => {
                                    if (table.reportSeriesTableID === item.reportSeriesTableID) {
                                        table.columns.forEach((col) => {
                                            if (
                                                col.columnName === item.columnName ||
                                                insight.tableDrillAttributes.includes(
                                                    col.columnName
                                                )
                                            ) {
                                                insight.selectedTableColumns.push({
                                                    ...col,
                                                    reportSeriesTableID: table.reportSeriesTableID,
                                                })
                                            }
                                        })
                                    }
                                })
                            })
                        } else {
                            // KPI
                            workspaceMetadata.kpis.forEach((kpi) => {
                                if (kpi.metricName === item.kpi) insight.selectedTableKpis.push(kpi)
                                workspaceMetadata?.inboxes?.forEach((inbox) => {
                                    inbox.tables.forEach((table) => {
                                        if (table.reportSeriesTableID === kpi.reportSeriesTableID) {
                                            table.columns.forEach((col) => {
                                                if (
                                                    insight.tableDrillAttributes.includes(
                                                        col.columnName
                                                    )
                                                ) {
                                                    insight.selectedTableColumns.push({
                                                        ...col,
                                                        reportSeriesTableID:
                                                            table.reportSeriesTableID,
                                                    })
                                                }
                                            })
                                        }
                                    })
                                })
                            })
                        }
                    }
                })
            }
        }
        if (!insight.displayRows) insight.displayRows = 50
        if (!insight.worksheetColumns) insight.worksheetColumns = []
        if (!insight.styleOverrides) insight.styleOverrides = { ...DEFAULT_CHART_PREFERENCES }
        merge(insight.styleOverrides, DEFAULT_CHART_PREFERENCES)
        // FOR BACKWARDS COMPATIBILITY
        if (!Array.isArray(insight.styleOverrides.xAxis))
            insight.styleOverrides.xAxis = cloneDeep([insight.styleOverrides.xAxis])
        if (!Array.isArray(insight.styleOverrides.yAxis))
            insight.styleOverrides.yAxis = cloneDeep([insight.styleOverrides.yAxis])
        if (!insight.styleOverrides.dimensions) {
            if (insight.styleOverrides.title.textStyle.fontScaleFactor) {
                if (insight.view !== 'kpi') {
                    insight.styleOverrides.dimensions = {
                        width:
                            (insight.styleOverrides.title.textStyle.fontSize ??
                                TITLE_DEFAULT_VALUES.textStyle.fontSize) /
                            insight.styleOverrides.title.textStyle.fontScaleFactor.x,
                        height:
                            (insight.styleOverrides.title.textStyle.fontSize ??
                                TITLE_DEFAULT_VALUES.textStyle.fontSize) /
                            insight.styleOverrides.title.textStyle.fontScaleFactor.y,
                    }
                } else {
                    insight.styleOverrides.dimensions = {
                        width: 200,
                        height: 200,
                    }
                }
            } else {
                if (insight.view !== 'kpi') {
                    insight.styleOverrides.dimensions = {
                        width: window.innerWidth - 600,
                        height: window.innerHeight - 112,
                    }
                } else {
                    insight.styleOverrides.dimensions = {
                        width: 200,
                        height: 200,
                    }
                }
            }
            removeFontScaleFactors(insight)
        }
        if (!insight.styleOverrides.pie || !insight.styleOverrides.donut) {
            insight.styleOverrides.pie = { ...DEFAULT_CHART_PREFERENCES.pie }
            insight.styleOverrides.donut = {
                ...DEFAULT_CHART_PREFERENCES.donut,
            }
        }
        if (!insight.styleOverrides.pie.itemStyle) {
            insight.styleOverrides.pie.itemStyle = {
                ...DEFAULT_CHART_PREFERENCES.pie.itemStyle,
            }
            insight.styleOverrides.donut.itemStyle = {
                ...DEFAULT_CHART_PREFERENCES.donut.itemStyle,
            }
        }
        if (!insight.styleOverrides.pie.label) {
            insight.styleOverrides.pie.label = {
                ...DEFAULT_CHART_PREFERENCES.pie.label,
            }
            insight.styleOverrides.donut.label = {
                ...DEFAULT_CHART_PREFERENCES.donut.label,
            }
        }
        if (!insight.sortColumns) insight.sortColumns = []
        if (!insight.kpiCompareType) {
            insight.kpiCompareType = 'percentage'
            insight.kpiCompareTarget = undefined
        }
        if (insight.worksheetID && !insight.backwardsFlag) {
            insight.backwardsFlag = backwardsFlag
            insight.selectedTableColumns = insight.selectedTableColumns.map((c) => {
                if (c.isMeasure && c.reportSeriesTableID.includes('orphan'))
                    return {
                        ...c,
                        worksheetID: insight.worksheetID,
                        rangeName: insight.rangeName,
                    }
                else return c
            })
            insight.selectedItems = insight.selectedItems
                .map((i) => ({
                    measureName: i.measureName || i.columnName,
                    worksheetID: insight.worksheetID,
                    rangeName: insight.rangeName,
                }))
                .filter((i) => i.measureName)
        }
        if (!insight.metricsOrder) insight.metricsOrder = []
        // END temp backwards stuff
        if (!(insight.selectedDates instanceof Map)) {
            if (insight.selectedDates) {
                insight.selectedDates = new Map(Object.entries(insight.selectedDates))
            } else {
                insight.selectedDates = new Map()
            }
        }
        setConfig(insight)
        if (!fromChat) setIsExistingInsight(true)
        if (insight.view === 'chart') {
            chartState.getResults({ ...insight }, () => {
                setLoadingChartInsight(false)
                setLoadingTableInsight(false)
            })
        }
    }

    const handleCreateFilter = (column) => {
        if (!advanced) handleAdvancedSwitch()
        setSelectedLeftTab('filters')
        setFilterOpen(true)
        setFilterSelectedColumn(column)
    }

    const resetFilterState = () => {
        setFilterOpen(undefined)
        setFilterSelectedColumn(undefined)
    }

    const shouldRenderInsight = () => {
        if (config.view === 'table')
            return (
                config.selectedTableColumns.length > 0 ||
                config.selectedTableKpis.length > 0 ||
                config.worksheetID
            )
        if (config.view === 'chart') {
            return config.selectedItems.length > 0 && chartState.hasData()
        }
        if (config.view === 'kpi') return config.selectedItems.length > 0
        return true
    }

    const getTheme = () => {
        if (config && config.themeID) {
            if (config.themeID === 'defaultTheme') {
                return {
                    themeID: 'defaultTheme',
                    themeName: 'Default theme',
                    colorScheme: {
                        backgroundColor: ScoopTheme.backgroundColor,
                        colors: ScoopTheme.color,
                        darkTheme: false,
                    },
                }
            }
            return getWorkspaceTheme(config.themeID, workspaceMetadata)
        }
        return undefined
    }

    const getSummaryTitle = () => {
        if (config?.insightName && config?.insightName.length > 0) {
            return config.insightName
        } else if (config.chartTitle && config?.chartTitle.length > 0) {
            return config.chartTitle
        }
    }

    const getInsightBackgroundClass = () => {
        const theme = getTheme()
        if (shouldRenderInsight()) {
            if (
                theme?.colorScheme?.backgroundColor &&
                (theme.colorScheme.backgroundColor === 'rgba(0,0,0,0)' ||
                    theme.colorScheme.backgroundColor === '#00000000' ||
                    theme.colorScheme.backgroundColor.slice(-2) === '00')
            ) {
                if (theme?.colorScheme?.darkTheme) {
                    return styles.insightContainerDark
                } else {
                    return styles.insightContainerLight
                }
            }
        }
        return ''
    }

    const handleSaveInsightChanges = (goBack, config) => {
        const returnToCanvas = !!state?.editmodeConfig
        const configToSave = cloneDeep(config)
        if (configToSave.view === 'chart' && chartState.valueAxis)
            configToSave.metricsOrder = chartState.valueAxis.map((a) => ({
                name: a.name,
                position: a.position,
            }))
        if (configToSave.view !== 'kpi') {
            configToSave.styleOverrides.dimensions = {
                width: window.innerWidth - 600,
                height: window.innerHeight - 112,
            }
        } else {
            configToSave.styleOverrides.dimensions = {
                width: 200,
                height: 200,
            }
        }
        saveInsight(
            returnToCanvas ? state?.insightKey : configToSave.insightKey,
            returnToCanvas ? state?.insightName : configToSave.insightName,
            workspaceID,
            userID,
            configToSave,
            postData,
            () => {
                if (goBack) {
                    if (returnToCanvas) navigate(ROUTES.CANVAS_DASHBOARD + '/' + state?.canvasID)
                }
            }
        )
    }

    const handleOnGenerateWithAISuccess = (insight) => {
        getWorkspaceMetadata(chartState)
        loadFromSavedInsight(insight.insightObject)
    }

    const handleGoToDataset = () => {
        navigate(ROUTES.SOURCES, { state: { openNewDataset: true } })
    }

    return (
        <div className={styles.screenContainer}>
            <div className={styles.explorerNav}>
                <div className={styles.explorerTitleContainer}>
                    <ScoopText className={styles.explorerTitle}>
                        Explorer
                        <HelpBotPromptButton
                            prompt={HELP_BOT_PROMPTS.EXPLORER_SCREEN}
                            variant="icon"
                            iconMinimal
                            tooltip="Visualize your data with charts and tables. Click to learn more."
                            tooltipPlacement="right"
                        />
                    </ScoopText>
                </div>
                <ScoopText className={styles.summaryTitle}>{getSummaryTitle()}</ScoopText>
                {!workspaceMetadataLoading &&
                    workspaceMetadata?.inboxes?.length > 0 &&
                    !isTourWorkspace && (
                        <div className={styles.explorerNavActions}>
                            {!workspaceMetadata?.account?.accountType?.startsWith('appsumo') && (
                                <ScoopButton
                                    variant="outline"
                                    size="xs"
                                    className={styles.navButton}
                                    leftSection={<MagicWandIcon size={18} weight="fill" />}
                                    onClick={() => {
                                        trackEvent(EVENTS.EXPLORER.ASK_AI)
                                        generateModal.show({
                                            inboxes: workspaceMetadata?.inboxes || [],
                                            onGenerateSuccess: handleOnGenerateWithAISuccess,
                                            onGenerateError: (error) =>
                                                toast.error(
                                                    typeof error === 'string'
                                                        ? error
                                                        : 'Error generating summary'
                                                ),
                                            generateInsight: true,
                                        })
                                    }}
                                >
                                    Ask AI
                                </ScoopButton>
                            )}
                            <ScoopButton
                                name={'load-insight-button'}
                                variant="outline"
                                size="xs"
                                className={styles.navButton}
                                leftSection={<FolderOpenIcon size={18} weight="fill" />}
                                onClick={() =>
                                    openModal.show({
                                        setInsight: loadFromSavedInsight,
                                        workspaceMetadata,
                                        server,
                                        window: 'Explorer',
                                        resetState: handleReset,
                                    })
                                }
                            >
                                Open
                            </ScoopButton>
                            {(config.selectedItems?.length > 0 ||
                                (config.worksheetID && config.rangeName) ||
                                config.selectedTableColumns.length > 0 ||
                                config.selectedTableKpis.length > 0) && (
                                <ScoopButton
                                    variant="outline"
                                    size="xs"
                                    className={`${styles.navButton} ${isOnboardingStep3 ? styles.glowButton : ''}`}
                                    leftSection={<FloppyDiskBackIcon size={18} weight="fill" />}
                                    onClick={handleSaveInsight}
                                >
                                    Save As
                                </ScoopButton>
                            )}
                            {isExistingInsight && (
                                <ScoopButton
                                    variant="outline"
                                    size="xs"
                                    className={styles.navButton}
                                    leftSection={<FloppyDiskIcon size={18} weight="fill" />}
                                    onClick={() =>
                                        saveChangesModal.show({
                                            handleSave: () => {
                                                if (!!state?.editmodeConfig || config.insightKey)
                                                    handleSaveInsightChanges(true, config)
                                            },
                                            handleDontSave: () => {},
                                        })
                                    }
                                >
                                    Save
                                </ScoopButton>
                            )}
                            {(config.worksheetID ||
                                config.selectedItems.length > 0 ||
                                config.selectedTableColumns.length > 0 ||
                                config.selectedTableKpis.length > 0) && (
                                <ScoopButton
                                    size="xs"
                                    className={styles.resetInsight}
                                    onClick={() =>
                                        resetInsightModal.show({
                                            onConfirm: handleReset,
                                        })
                                    }
                                >
                                    Reset
                                </ScoopButton>
                            )}
                        </div>
                    )}
            </div>
            <div className={styles.explorerContent}>
                {!workspaceMetadataLoading && workspaceMetadata?.inboxes?.length === 0 ? (
                    <div className={styles.emptyDatasetContainer}>
                        <EmptyDataset
                            title={'Welcome to Explorer'}
                            description={
                                "Dive deep into your data with Scoop's Explorer—analyze, slice, and summarize your dataset to create stunning visual summaries and uncover key insights."
                            }
                            iconComponent={
                                <div className={styles.emptyIconWrapper}>
                                    <ChartBarIcon
                                        size={32}
                                        weight="duotone"
                                        className={styles.iconDark}
                                    />
                                </div>
                            }
                            onClick={handleGoToDataset}
                        />
                    </div>
                ) : (
                    <>
                        <ScoopDrawer
                            open={dataTabOpen}
                            anchor="left"
                            variant="persistent"
                            width={300}
                        >
                            <div className={styles.tabContainer}>
                                <ScoopTab
                                    tabs={leftDrawerTabs}
                                    value={selectedLeftTab}
                                    onChange={(e, value) => setSelectedLeftTab(value)}
                                />
                            </div>
                            <div className={styles.drawerContent}>
                                {selectedLeftTab === 'data' && (
                                    <DataTab
                                        config={config}
                                        setConfig={setConfig}
                                        chartState={chartState}
                                        server={server}
                                        getWorkspaceMetadata={(callback) =>
                                            getWorkspaceMetadata(chartState, callback)
                                        }
                                        workspaceID={workspaceID}
                                        worksheetMetrics={worksheetMetrics}
                                        setWorksheetMetrics={setWorksheetMetrics}
                                    />
                                )}
                                {selectedLeftTab === 'filters' && (
                                    <FilterTab
                                        config={config}
                                        setConfig={setConfig}
                                        chartState={chartState}
                                        advanced={advanced}
                                        workspaceMetadata={workspaceMetadata}
                                        server={server}
                                        getWorkspaceMetadata={() =>
                                            getWorkspaceMetadata(chartState)
                                        }
                                        analyzeChanges={analyzeChanges}
                                        initialOpen={filterOpen}
                                        initialSelectedColumn={filterSelectedColumn}
                                        resetFilterState={resetFilterState}
                                        onOpenTimeRange={() =>
                                            timeRangeModal.show({
                                                config,
                                                setConfig,
                                                chartProperties: chartState,
                                            })
                                        }
                                    />
                                )}
                            </div>
                        </ScoopDrawer>
                        <ScoopDrawer
                            open={configTabOpen}
                            anchor="right"
                            variant="persistent"
                            width={300}
                        >
                            <div className={styles.tabContainer}>
                                <ScoopTab
                                    tabs={rightDrawerTabs}
                                    value={selectedRightTab}
                                    onChange={(e, value) => setSelectedRightTab(value)}
                                />
                            </div>
                            <div
                                className={`${styles.drawerContent} ${styles.drawerContentCustom}`}
                            >
                                {selectedRightTab === 'properties' && (
                                    <ConfigTab
                                        config={config}
                                        setConfig={setConfig}
                                        chartState={chartState}
                                        advanced={advanced}
                                        handleAdvancedToggle={handleAdvancedSwitch}
                                        analyzeChanges={analyzeChanges}
                                        setAnalyzeChanges={setAnalyzeChanges}
                                        workspaceMetadata={workspaceMetadata}
                                        server={server}
                                        getWorkspaceMetadata={() =>
                                            getWorkspaceMetadata(chartState)
                                        }
                                        columnLoading={columnLoading}
                                        setColumnLoading={setColumnLoading}
                                        dateFlag={dateFlag}
                                        setDateFlag={setDateFlag}
                                        onOpenTimeRange={() =>
                                            timeRangeModal.show({
                                                config,
                                                setConfig,
                                                chartProperties: chartState,
                                            })
                                        }
                                    />
                                )}
                                {selectedRightTab === 'style' && (
                                    <StyleTab
                                        config={config}
                                        setConfig={setConfig}
                                        chartState={chartState}
                                        setChartState={setChartState}
                                        workspaceMetadata={workspaceMetadata}
                                        getWorkspaceMetadata={(callback) =>
                                            getWorkspaceMetadata(chartState, callback)
                                        }
                                        server={server}
                                        handleSaveInsight={(config) =>
                                            handleSaveInsightChanges(false, config)
                                        }
                                    />
                                )}
                            </div>
                        </ScoopDrawer>
                        <div
                            className={`${styles.insightContainer} ${getInsightBackgroundClass()}`}
                        >
                            {loadingChartInsight || loadingTableInsight ? (
                                <div className={styles.loadingContainer}>
                                    <ScoopSpinner size={56} />
                                </div>
                            ) : shouldRenderInsight() ? (
                                <Insight
                                    chartProperties={chartState}
                                    setChartProperties={setChartState}
                                    config={config}
                                    setConfig={setConfig}
                                    server={server}
                                    advanced={advanced}
                                    handleAdvancedToggle={handleAdvancedSwitch}
                                    analyzeChanges={analyzeChanges}
                                    handleCreateFilter={handleCreateFilter}
                                    setColumnLoading={setColumnLoading}
                                    workspaceMetadata={workspaceMetadata}
                                    dateFlag={dateFlag}
                                    theme={getTheme()}
                                    clickable
                                    setDataTabOpen={setDataTabOpen}
                                    dataTabOpen={dataTabOpen}
                                    setConfigTabOpen={setConfigTabOpen}
                                    configTabOpen={configTabOpen}
                                />
                            ) : (
                                <div className={styles.noDataContainer}>
                                    <img src={ChartPlaceholder} alt={'chart-placeholder'} />
                                    <ScoopText className={styles.noDataTitle}>
                                        No data input
                                    </ScoopText>
                                    <ScoopText className={styles.noDataSubtitle}>
                                        Select a KPI or Source Metric to get started
                                    </ScoopText>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
